<div class="dashboard-container">
    <!-- Header Section -->
    <header class="header-section">
        <h1>Welcome back, {{ userName }}</h1>
        <p>Here's what's happening with your sessions today.</p>
    </header>

    <!-- Stats Grid -->
    <div class="stats-grid" *ngIf="!isLoadingStats; else statsSkeleton">
        <div class="stat-card" *ngFor="let stat of stats">
            <div class="icon-box" [ngClass]="stat.colorClass">
                <i class="bi" [ngClass]="stat.icon"></i>
            </div>
            <div class="stat-info">
                <span class="count">{{ stat.count }}</span>
                <span class="label">{{ stat.label }}</span>
            </div>
        </div>
    </div>
    <ng-template #statsSkeleton>
        <div class="stats-grid">
            <div class="stat-card skeleton-card" *ngFor="let i of [1,2,3,4,5]">
                <div class="icon-box skeleton-shimmer"></div>
                <div class="stat-info">
                    <div class="skeleton-shimmer skeleton-count"></div>
                    <div class="skeleton-shimmer skeleton-label"></div>
                </div>
            </div>
        </div>
    </ng-template>

    <!-- Sessions Section -->
    <div class="sessions-section">
        <div class="section-header">
            <div class="title-with-count">
                <h2>Today's Sessions</h2>
                <span class="count-badge" *ngIf="!isLoadingSessions">{{ filteredSessions.length }}</span>
                <span class="count-badge skeleton-shimmer skeleton-mini-count" *ngIf="isLoadingSessions"></span>
            </div>
            <a [routerLink]="['/mentor-panel/upcoming-sessions']" class="view-all">View All <i
                    class="bi bi-arrow-right"></i></a>
        </div>

        <div class="sessions-grid" *ngIf="isLoadingSessions">
            <div class="session-card skeleton-session-card" *ngFor="let i of [1,2,3]">
                <div class="card-header">
                    <div class="skeleton-shimmer skeleton-badge"></div>
                    <div class="skeleton-shimmer skeleton-badge"></div>
                </div>
                <div class="user-info">
                    <div class="avatar skeleton-shimmer"></div>
                    <div class="name-box">
                        <div class="skeleton-shimmer skeleton-name"></div>
                        <div class="skeleton-shimmer skeleton-subtle"></div>
                    </div>
                </div>
                <div class="session-content">
                    <div class="skeleton-shimmer skeleton-topic"></div>
                    <div class="details">
                        <div class="skeleton-shimmer skeleton-detail"></div>
                        <div class="skeleton-shimmer skeleton-detail"></div>
                    </div>
                </div>
                <div class="card-actions">
                    <div class="skeleton-shimmer skeleton-btn-large"></div>
                    <div class="skeleton-shimmer skeleton-btn-small"></div>
                </div>
            </div>
        </div>

        <div class="sessions-grid" *ngIf="!isLoadingSessions && filteredSessions.length > 0; else noSessions">
            <div class="session-card" *ngFor="let session of filteredSessions" [class.highlight-card]="session.isToday">
                <div class="card-header">
                    <span class="badge today-badge" *ngIf="session.isToday">
                        <span class="dot"></span> TODAY
                    </span>
                    <span class="type-badge">{{ session.status }}</span>
                </div>

                <div class="user-info">
                    <div class="avatar" [ngClass]="session.avatarColorClass">
                        {{ session.initials }}
                    </div>
                    <div class="name-box">
                        <h3>{{ session.studentName }}</h3>
                        <span class="session-type">{{ session.sessionType }}</span>
                    </div>
                </div>

                <div class="session-content">
                    <p class="topic">{{ session.topic }}</p>
                    <div class="details">
                        <span><i class="bi bi-calendar3"></i> {{ session.date }}</span>
                        <span><i class="bi bi-clock"></i> {{ session.time }}</span>
                        <span><i class="bi bi-hourglass-split"></i> {{ session.duration }}</span>
                    </div>
                </div>

                <div class="card-actions">
                    <button class="btn btn-join" (click)="joinMeeting(session)">
                        <i class="bi bi-camera-video"></i> Join Meeting
                    </button>
                    <button class="btn btn-complete" (click)="markComplete(session)">
                        <i class="bi bi-check-circle"></i> Complete
                    </button>
                </div>
            </div>
        </div>

        <ng-template #noSessions>
            <div class="no-sessions-card">
                <div class="empty-icon">
                    <i class="bi" [ngClass]="searchQuery ? 'bi-search' : 'bi-calendar-x'"></i>
                </div>
                <h3>{{ searchQuery ? 'No matching sessions found' : 'No sessions available for today' }}</h3>
                <p>{{ searchQuery ? 'Try searching with a different name or topic.' : 'Enjoy your free time! Your
                    today\'s sessions will appear here.' }}</p>
                <button *ngIf="searchQuery" class="btn btn-sm btn-outline-secondary mt-3"
                    (click)="searchQuery = ''">Clear Search</button>
            </div>
        </ng-template>
    </div>

    <!-- Recent Session Requests Table -->
    <div class="requests-section">
        <div class="section-header">
            <h2>Recent Session Requests</h2>
        </div>

        <div class="requests-card">
            <div class="table-controls">
                <div class="search-box">
                    <i class="bi bi-search"></i>
                    <input type="text" placeholder="Search by student or topic..." [(ngModel)]="searchQuery">
                </div>
                <a [routerLink]="['/mentor-panel/session-requests']" class="view-all">View All <i
                        class="bi bi-arrow-right"></i></a>
            </div>

            <div class="table-responsive">
                <table class="table requests-table">
                    <thead>
                        <tr>
                            <th>STUDENT</th>
                            <th>DATE</th>
                            <th>TIME</th>
                            <th>DURATION</th>
                            <th>TOPIC</th>
                            <th>CATEGORY</th>
                            <th class="text-end">STATUS / ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody *ngIf="isLoadingRequests">
                        <tr *ngFor="let i of [1,2,3]">
                            <td>
                                <div class="student-cell">
                                    <div class="avatar-sm skeleton-shimmer"></div>
                                    <div class="skeleton-shimmer skeleton-text-name"></div>
                                </div>
                            </td>
                            <td>
                                <div class="skeleton-shimmer skeleton-text-wide"></div>
                            </td>
                            <td>
                                <div class="skeleton-shimmer skeleton-text-wide"></div>
                            </td>
                            <td>
                                <div class="skeleton-shimmer skeleton-text-wide"></div>
                            </td>
                            <td>
                                <div class="skeleton-shimmer skeleton-text-wide"></div>
                            </td>
                            <td>
                                <div class="skeleton-shimmer skeleton-badge-sm"></div>
                            </td>
                            <td class="text-end">
                                <div class="action-buttons">
                                    <div class="skeleton-shimmer skeleton-btn-inline"></div>
                                    <div class="skeleton-shimmer skeleton-btn-inline"></div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody *ngIf="!isLoadingRequests">
                        <tr *ngFor="let request of filteredRequests">
                            <td>
                                <div class="student-cell">
                                    <div class="avatar-sm" [ngClass]="request.avatarColorClass">
                                        {{ request.initials }}
                                    </div>
                                    <span class="student-name">{{ request.studentName }}</span>
                                </div>
                            </td>
                            <td data-label="DATE"><span class="date-text">{{ request.date }}</span></td>
                            <td data-label="TIME"><span class="time-text">{{ request.time }}</span></td>
                            <td data-label="DURATION"><span class="duration-pill">{{ request.duration }}</span></td>
                            <td data-label="TOPIC"><span class="topic-text">{{ request.topic }}</span></td>
                            <td data-label="CATEGORY"><span class="category-text">{{ request.category }}</span></td>
                            <td class="text-end" data-label="ACTIONS">
                                <ng-container
                                    *ngIf="request.status === SessionStatus.AWAITING_ACCEPTANCE; else statusBadge">
                                    <div class="action-buttons">
                                        <button class="btn btn-accept" (click)="acceptRequest(request)"
                                            title="Accept Request">
                                            <i class="bi bi-check-circle"></i> Accept
                                        </button>
                                        <button class="btn btn-reject" (click)="rejectRequest(request)"
                                            title="Reject Request">
                                            <i class="bi bi-x-circle"></i> Reject
                                        </button>
                                    </div>
                                </ng-container>
                                <ng-template #statusBadge>
                                    <app-status-badge [status]="request.status"></app-status-badge>
                                </ng-template>
                            </td>
                        </tr>
                        <!-- No Results for Search -->
                        <tr *ngIf="requests.length > 0 && filteredRequests.length === 0">
                            <td colspan="7" class="text-center py-5">
                                <div class="no-data">
                                    <i class="bi bi-search fs-3 text-muted d-block mb-3"></i>
                                    <p class="text-muted fw-medium">No results found for "{{ searchQuery }}"</p>
                                    <button class="btn btn-sm btn-outline-secondary mt-2"
                                        (click)="searchQuery = ''">Clear
                                        Search</button>
                                </div>
                            </td>
                        </tr>

                        <!-- No Requests at All -->
                        <tr *ngIf="requests.length === 0">
                            <td colspan="7" class="text-center py-5">
                                <div class="no-data">
                                    <i class="bi bi-inbox fs-1 text-muted d-block mb-3"></i>
                                    <p class="text-muted fw-medium">No session requests available at the moment.</p>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</div>